import { storage } from '@abyss/web/tools/storage';
import { getEnglishModule } from '@uhg-psx/mapping-files-translation-transperfect';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { deriveChipValueIfMissing } from '../../common/BookmarkButton/Utils';
import {
  CareCategories,
  Constants,
  DEFAULT_SEARCH_RADIUS,
  EAP_OPTION_LABEL,
  FACILITIES_M1,
  FilterCollectionModel,
  ProviderType,
  ProviderTypes,
  SectionType,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { applyBhAoeTranslations } from '../../common/ContentFilters/AllFilterDrawer/ProviderFilters/GroupedAoeFilter/groupedAoe.utils';
import { convertTypeStringToArray } from '../../common/Utils/capitalizeFirstLetter';
import {
  getBrowseLabsAndServicesPreSelectFilters,
  getBrowseServicesPreSelectedFilters,
  getDistanceFilterOption,
  getIsVirtualCareV2,
  getIssVirtualCareSelected,
  getVirtualCareModel,
} from '../../common/Utils/filters.utils';
import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { useConfig } from '../../frontends/ProviderSearch/context/Analytics';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { getEnableMapPinRedesign } from '../../frontends/ProviderSearch/routes/MapView/utils';
import {
  getAgesTreatedFilterOptions,
  getConditionAoeCodes,
  getVirtualCareCode,
} from '../../frontends/ProviderSearch/routes/SearchResults/SearchResults.UtilsV2';
import {
  getAoeCodesForEAP,
  getBehavioralProgramId,
  getUpdatedAoeCodesWithEAP,
} from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import {
  Option,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../models/ProviderSearch';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { usePlanSelectorStore } from '../../store/usePlanSelectorStore';
import { useSearchStore } from '../../store/useSearchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { useVirtualCareStore } from '../../store/useVirtualCareStore';
import { ternaryExpression } from '../../utils/common';
import { getMapViewPageSize } from '../../utils/map.utils';
import {
  getCategoryVirtualCareProvidersFromEligibilities,
  getLagoonConfigValue,
  getRollupCodesForVCProviderSearch,
  transformFiltersData,
} from '../../utils/providerSearch.utils';
import {
  getIncludeSpecialityRollupCodesFilteredByCategory,
  getOrgTypeCodes,
  getProviderTypeBySectionType,
  resolveIsPCPLandingPage,
  resolvePcpIndicator,
} from '../../utils/typeahead.utils';
import { getCurrentMember, getOnlineRetailers } from '../../utils/user.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLagoon } from '../useLagoon';
import { useParsedProviderFilters } from '../useProviderFilterCount';
import { useProviderFilters } from '../useProviderFilters';
import { useFilterProviderResults } from '../useProviderSearch';
import { useSmartChoice } from '../useSmartChoice';
import { useSnackCardProviderResults } from '../useSnackCardProviderSearch/useSnackCardProviderSearch';
import { useTypeAheadData } from '../useTypeAheadData';
import { resolveIsLAWWOrSupportFinderDeeplink } from '../useTypeAheadData/utils';

const getIncludeSpecialityRollupCodes = (
  searchType: string,
  includeSpecialityRollupCodesFilteredByCategory: string[],
  combinedIncludeSpecialityRollupCodes: string,
  choosePCP: boolean
) => {
  if (choosePCP) {
    return combinedIncludeSpecialityRollupCodes;
  } else if (searchType === 'name') {
    return '';
  }
  return includeSpecialityRollupCodesFilteredByCategory.join(',');
};

export const getRollupCodeParams = ({
  isFqhAndrhcSearch,
  isSpecialtyCodeSearch,
  specialtyCode,
  enableInclSpecCodeChanges,
}) => {
  if (isFqhAndrhcSearch) {
    return { includeSpecialityRollupCodes: specialtyCode };
  }
  if (isSpecialtyCodeSearch) {
    return {
      includeSpecialityRollupCodes: specialtyCode,
      includeSpecRollupCodesArray: enableInclSpecCodeChanges
        ? specialtyCode?.split(',')
        : null,
    };
  }
  return {};
};

export const compareAndReturnAsNumber = (
  value1: string | number,
  value2: string | number,
  fallbackValue: number = NaN
) => {
  const num1 = Number(value1);
  if (value1 != null && !isNaN(num1)) {
    return num1;
  }

  const num2 = Number(value2);
  if (value2 != null && !isNaN(num2)) {
    return num2;
  }

  return fallbackValue;
};

export const getANPFilter = ({
  isPrimaryCareProvidersSearch,
  isPrimaryCareProviderGroupsSearch,
  isFqhAndrhcSearch,
  disablePCPIndAndANP,
}) => {
  if (isFqhAndrhcSearch) {
    return [FilterCollectionModel.ACCEPTING_NEW_PATIENTS];
  }
  if (isPrimaryCareProvidersSearch || isPrimaryCareProviderGroupsSearch) {
    return disablePCPIndAndANP
      ? []
      : [FilterCollectionModel.ACCEPTING_NEW_PATIENTS];
  }
  return [];
};

export const getHeading = ({ psxSource, heading, chipValue }) => {
  if (heading) {
    return heading;
  }

  const headingMap = {
    [Constants.LANDING_PAGE_SEARCH_SOURCE_TYPES.PRIMARY_CARE_PROVIDERS]:
      chipValue || '',
    [Constants.LANDING_PAGE_SEARCH_SOURCE_TYPES.PRIMARY_CARE_PROVIDER_GROUPS]:
      'TOP_PRIMARY_CARD_MEDICAL_GROUPS.HEADING',
  };
  return headingMap[psxSource] || '';
};

export const getInitialPageResults = (
  sectionType: SectionType,
  providersResult: any,
  medGrpResult: any,
  organizationResult: any
) => {
  if (sectionType === SectionType.PROVIDER) {
    return providersResult;
  }
  if (sectionType === SectionType.PROVIDER_GROUP) {
    return medGrpResult;
  }
  return organizationResult;
};

export const useViewAllData = ({
  pageNumber,
  pageSize,
  coverageType,
  shouldGetHGData,
  mapLocationsOnly = false,
  providerId = '',
  extendedPinsDrawerReq = false,
  providerLocationId = '',
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { portalData } = useContext(PortalContext);
  const { setDefaultSearchFilters, setSearchFilters, searchFilters } =
    useContext(SearchFilterContext);
  const deepLinkMapping = useLagoon(Constants.LAGOON_TABLE.DEEPLINK_MAPPING)();
  const {
    landedOnDetailsPageFromSearch,
    setLandedOnDetailsPageFromSearch,
    chipValue: chipValueFromStore,
    setChipValue,
  } = useChipStore(
    useShallow((state: ChipState) => ({
      landedOnDetailsPageFromSearch: state.landedOnDetailsPageFromSearch,
      setLandedOnDetailsPageFromSearch: state.setLandedOnDetailsPageFromSearch,
      chipValue: state.chipValue,
      setChipValue: state.setChipValue,
    }))
  );
  const [
    enableBHVirtualCareFilterV2,
    disablePCPIndAndANPFlag,
    disablePCPIndAndANPEarlyPayFlag,
    enableInclSpecCodeChanges,
    eapBHProgramFlag,
    enableMapPinRedesignFlag,
    enablePlanTypeSelectionInFilterFlag,
    cleanUpProviderCalls,
    enableBhGroupedAoeFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PCP_IND_AND_ANP_EARLYPAY,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_INCLUDE_SPEC_CODE_CHANGES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_EAP_FILTER_USING_BH_PROGRAM,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_TYPE_SELECTION_IN_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.CLEANUP_PROVIDER_CALLS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
  ]);

  const [isProcessingResults, setIsProcessingResults] = useState(false);

  const currentMember = getCurrentMember();

  const disablePCPIndAndANP = currentMember?.isIFPEarlyPay
    ? disablePCPIndAndANPEarlyPayFlag
    : disablePCPIndAndANPFlag;

  const aoeTranslations = getEnglishModule('aoeTranslations');
  const uesTranslation = getEnglishModule('uesSpecialityTranslations');
  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const {
    search,
    searchType,
    chipValue,
    sectionType,
    choosePCP: choosePCPFromParams,
    searchRadius: searchRadiusFromParams,
    [Constants.SOURCE]: sourceFromParams,
    [Constants.PSX_SOURCE]: psxSource,
    [Constants.SOURCE]: sourceFromGlobalSearch,
    [Constants.PRE_SELECT_FILTERS]: preSelectFiltersFromParams,
    platinumProgramCode: platinumProgramCodeFromParams,
    bhProgramFacilityAgg: bhProgramFacilityAggFromParams,
    networkAffiliationId: networkAffiliationIdFromParams,
  } = searchParamsObject;
  const skipPreSelectingFilters = preSelectFiltersFromParams === 'false';
  const choosePCP = choosePCPFromParams === 'true';
  const providerType = getProviderTypeBySectionType(sectionType as SectionType);
  const viewAllDataTestId = `results-section-${sectionType}-view-all`.replace(
    / /g,
    '-'
  );

  const isGlobalSearch =
    sourceFromParams === Constants.SEARCH_SOURCE_TYPES.GLOBAL_SEARCH;

  const isLagoonSource = psxSource?.split(':')[0] === 'lagoon';
  const lagoonTableName = isLagoonSource ? psxSource.split(':')[1] : '';
  const maybeLagoonData = useLagoon(lagoonTableName);
  const lagoonData =
    typeof maybeLagoonData === 'function' ? maybeLagoonData() : [];

  const isLAWWOrSupportFinderDeeplink = resolveIsLAWWOrSupportFinderDeeplink(
    portalData,
    searchParamsObject
  );

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );
  const enableMapPinRedesign = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const mapPinsPageSizeConfig = useConfig(
    ConstantsLagoon.CONFIG.MAP_PINS_PAGE_SIZE
  );
  const smartChoiceMapPinPageSizeConfig = useConfig(
    ConstantsLagoon.CONFIG.SMART_CHOICE_MAP_PINS_PAGE_SIZE
  );

  const isSmartChoice = useSmartChoice();

  const mapPinsPageSize = getMapViewPageSize(
    enableMapPinRedesign,
    isSmartChoice ? smartChoiceMapPinPageSizeConfig : mapPinsPageSizeConfig
  );

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: any) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setPCPSearchState, dependentSeqNbr } = usePCPStore(
    useShallow((state: any) => ({
      setPCPSearchState: state.setPCPSearchState,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );
  const { setSearchStore } = useSearchStore(
    useShallow((state: any) => ({
      setSearchStore: state.setSearchStore,
    }))
  );
  const {
    setTypeaheadSearchStore,
    isVisionRelatedSearch,
    isTypeaheadStoreValuesSet,
  } = useTypeaheadStore(
    useShallow((state: any) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
      isVisionRelatedSearch: state.typeaheadSearchStore.isVisionRelatedSearch,
      isTypeaheadStoreValuesSet:
        state.typeaheadSearchStore.isTypeaheadStoreValuesSet,
    }))
  );
  const planInfo = usePlanSelectorStore((state) => state.planInfo);

  const filtersCollection = useProviderFilters();
  const { providerFilterList } = useParsedProviderFilters({
    selectedFilters: searchFilters,
  });
  const bhProgramLagoonData = useLagoon('behavioral-facility-programs')();
  const aoeFiltersLagoon = useLagoon('aoe-filters')();
  const transformedFilters = transformFiltersData(
    t,
    filtersCollection as ProviderFiltersType[],
    providerFilterList,
    {},
    coverageType,
    {},
    planInfo,
    bhProgramLagoonData
  );

  const isPrimaryCareProvidersSearch =
    psxSource ===
    Constants.LANDING_PAGE_SEARCH_SOURCE_TYPES.PRIMARY_CARE_PROVIDERS;

  const isPrimaryCareProviderGroupsSearch =
    psxSource ===
    Constants.LANDING_PAGE_SEARCH_SOURCE_TYPES.PRIMARY_CARE_PROVIDER_GROUPS;

  const isFqhAndrhcSearch =
    psxSource === `lagoon:${Constants.LAGOON_TABLE.BROWSE_PRIMARY_CARE}`;

  const isPrimaryCareSearch =
    isPrimaryCareProvidersSearch ||
    isPrimaryCareProviderGroupsSearch ||
    isFqhAndrhcSearch;

  const isBrowseCommonSpecialtiesSearch =
    psxSource === `lagoon:${Constants.LAGOON_TABLE.BROWSE_COMMON_SPECIALTIES}`;

  const isBrowseBehaviorHealthProvidersSearch =
    psxSource ===
    `lagoon:${Constants.LAGOON_TABLE.BROWSE_BEHAVIORAL_HEALTH_PROVIDERS_V2}`;

  const isBrowseMedicalSupplyFacilitySearch =
    psxSource ===
    `lagoon:${Constants.LAGOON_TABLE.BROWSE_MEDICAL_SUPPLY_FACILITY}`;

  const isBrowseLabsAndServicesSearch =
    psxSource === `lagoon:${Constants.LAGOON_TABLE.BROWSE_LABS_AND_SERVICES}`;

  const isVisionCareSearch =
    psxSource === `lagoon:${Constants.LAGOON_TABLE.VISION_CARE}`;

  const isDentalCareSearch =
    psxSource ===
    `lagoon:${Constants.LAGOON_TABLE.BROWSE_DENTAL_PROVIDERS_LANDING_PAGE}`;

  const isSuggestedProvidersSearch =
    psxSource ===
    Constants.LANDING_PAGE_SEARCH_SOURCE_TYPES.SUGGESTED_PROVIDERS;

  const isLandingPageSearch =
    isPrimaryCareProvidersSearch ||
    isPrimaryCareProviderGroupsSearch ||
    isFqhAndrhcSearch ||
    isBrowseCommonSpecialtiesSearch ||
    isBrowseBehaviorHealthProvidersSearch ||
    isBrowseMedicalSupplyFacilitySearch ||
    isBrowseLabsAndServicesSearch ||
    isVisionCareSearch ||
    isDentalCareSearch ||
    isSuggestedProvidersSearch;

  const isSpecialtyCodeSearch =
    isBrowseCommonSpecialtiesSearch ||
    isBrowseBehaviorHealthProvidersSearch ||
    isBrowseMedicalSupplyFacilitySearch ||
    isBrowseLabsAndServicesSearch ||
    isVisionCareSearch ||
    isDentalCareSearch;

  const section = isLagoonSource
    ? lagoonData.find(({ DNT_heading, heading }) =>
        [DNT_heading?.toLowerCase(), heading?.toLowerCase()].includes(
          search?.toLowerCase()
        )
      )
    : { specialtyCode: '' };

  const { heading, specialtyCode, aoeIDs, virtualCare, programIDs } =
    section || {};

  const isPCPLandingPage = resolveIsPCPLandingPage(
    chipValue,
    psxSource,
    sourceFromGlobalSearch,
    networkAffiliationIdFromParams
  );

  const pcpIndicator = resolvePcpIndicator(
    disablePCPIndAndANP,
    isPCPLandingPage,
    choosePCP
  );

  useEffect(() => {
    if (psxSource || isGlobalSearch) {
      return;
    }
    const derivedChipValueLocal = deriveChipValueIfMissing(
      chipValueFromStore,
      chipValue,
      coverageType,
      sectionType,
      pcpIndicator
    );
    setChipValue(derivedChipValueLocal);
  }, [
    chipValue,
    chipValueFromStore,
    coverageType,
    sectionType,
    pcpIndicator,
    psxSource,
    setChipValue,
  ]);

  const selectedSearchRadius =
    searchFilters[FilterCollectionModel.DISTANCE]?.[0]?.value ||
    DEFAULT_SEARCH_RADIUS;
  const { planToken } = getPlanSelectorVariables();
  const defaultPlanTypeValue =
    Object.keys(planInfo).length > 1 ? planInfo?.[planToken] : {};
  const coverageTypeReciprocityId = (defaultPlanTypeValue as any)
    ?.coverageTypeReciprocityIdMap?.[coverageType];

  const planTypeRequired =
    enablePlanTypeSelectionInFilterFlag &&
    !landedOnDetailsPageFromSearch &&
    searchFilters.PlanType?.every(
      (item) => item.value !== coverageTypeReciprocityId
    )
      ? [FilterCollectionModel.PLAN_TYPE]
      : [];
  setLandedOnDetailsPageFromSearch(false);

  const uesVirtualCareKeywordMappingTable = useLagoon(
    'ues-virtual-care-keyword-mapping'
  )();
  const allConfigs = useLagoon('config');
  const enablePseudoRollupCodeOnlyMatch = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PSEUDO_ROLLUP_CODE_ONLY_MATCH'
  );
  const pseudoRollupCodeForPrimaryCareLandingPage = getLagoonConfigValue(
    allConfigs,
    'PSEUDO_ROLLUP_CODE_FOR_PRIMARYCARE_LANDING_SECTIONS'
  );
  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const setVirtualCareProviders = useVirtualCareStore(
    (state) => state.setVirtualCareProviders
  );

  useEffect(() => {
    if (
      selectedSearchRadius &&
      !extendedPinsDrawerReq &&
      skipPreSelectingFilters
    ) {
      setSearchParams(
        (searchParams) => {
          searchParams.set('searchRadius', selectedSearchRadius?.toString());
          return searchParams;
        },
        { replace: true }
      );
    }
  }, [selectedSearchRadius]);

  useEffect(() => {
    if (isLAWWOrSupportFinderDeeplink && !skipPreSelectingFilters) {
      const conditionFromUrl = searchParamsObject.condition as
        | string
        | undefined;
      applyBhAoeTranslations(
        aoeTranslations,
        coverageType,
        enableBhGroupedAoeFilter
      );
      const { areaOfExpertise: aoeFromUrl } = getConditionAoeCodes(
        conditionFromUrl,
        deepLinkMapping,
        aoeTranslations
      );
      const agesTreadedAoesFromUrl = getAgesTreatedFilterOptions(
        conditionFromUrl,
        deepLinkMapping,
        aoeFiltersLagoon
      );
      if (aoeFromUrl?.length > 0 || agesTreadedAoesFromUrl?.length > 0) {
        setDefaultSearchFilters(
          [
            ...(aoeFromUrl?.length > 0
              ? [FilterCollectionModel.AREA_OF_EXPERTISE]
              : []),
            ...(agesTreadedAoesFromUrl?.length > 0
              ? [FilterCollectionModel.AGES_TREATED]
              : []),
          ],
          {
            AgesAreaOfExpertise: agesTreadedAoesFromUrl,
            AreaOfExpertise: aoeFromUrl,
          }
        );
        setSearchParams(
          () => {
            const sp = new URLSearchParams(window.location.search);
            sp.set(Constants.PRE_SELECT_FILTERS, 'false');
            return sp;
          },
          { replace: true }
        );
      }
    }
  }, [isLAWWOrSupportFinderDeeplink, skipPreSelectingFilters]);

  const onMixedResultsPageResultsLoaded = (data: any) => {
    if (
      sectionType &&
      !(
        psxSource ||
        choosePCP ||
        isLAWWOrSupportFinderDeeplink ||
        isGlobalSearch
      )
    ) {
      setTypeaheadSearchStore({ isTypeaheadStoreValuesSet: true });
      setDefaultSearchFilters([]);
      return;
    }

    if (!data) {
      return;
    }

    setIsProcessingResults(true);
    const {
      providersResult,
      medGrpResult,
      organizationResult,
      typeAheadData,
      genderCode,
      virtualCareCode,
      essentialCommunityProviderCode,
      acceptingNewPatients,
      preSelectEapFilter,
      virtualCare,
      specialtyCodeFromQuery,
      specialtyCode,
      preferredFacilities: preferredFacility,
      invokeLlmModel,
    } = data;

    const results = getInitialPageResults(
      sectionType as SectionType,
      providersResult,
      medGrpResult,
      organizationResult
    );

    const { totalCount, searchRadius: searchRadiusFromQuery } = results || {};
    const { combinedIncludeSpecialityRollupCodes, condition, aoeCodes } =
      typeAheadData;
    applyBhAoeTranslations(
      aoeTranslations,
      coverageType,
      enableBhGroupedAoeFilter
    );
    const { areaOfExpertise } = getConditionAoeCodes(
      condition,
      deepLinkMapping,
      aoeTranslations
    );
    const agesTreadedAoes = getAgesTreatedFilterOptions(
      condition,
      deepLinkMapping,
      aoeFiltersLagoon
    );
    const searchRadius = virtualCare
      ? -1
      : compareAndReturnAsNumber(
          searchRadiusFromParams,
          searchRadiusFromQuery,
          DEFAULT_SEARCH_RADIUS
        );

    const {
      label = '',
      includeOrgTypeCodes = [],
      coverages = [],
      uesType = '',
    } = Constants.CARE_CATEGORIES_NAME_PAYLOAD.find(
      (category) => category.label === chipValue
    ) || {};
    const codeByGroup = combinedIncludeSpecialityRollupCodes?.split(',') || [];
    const categoryCoverageType = coverages?.[0]; // can we use coverageType ?

    const includeSpecialityRollupCodesFilteredByCategory: string[] =
      getIncludeSpecialityRollupCodesFilteredByCategory(
        uesTranslation,
        codeByGroup,
        uesType,
        sectionType,
        cleanUpProviderCalls
      );
    const orgTypeCodes = getOrgTypeCodes(
      label,
      includeOrgTypeCodes,
      FACILITIES_M1,
      searchType
    );

    const eapOption = areaOfExpertise?.filter(
      (option: Option) => option.label === EAP_OPTION_LABEL
    );
    const eapOptionValue = getAoeCodesForEAP().toString();
    const virtualCareModel = getVirtualCareModel(
      enableBHVirtualCareFilterV2,
      categoryCoverageType,
      sectionType
    );

    const isVirtualCareSelected = getIssVirtualCareSelected(
      searchFilters,
      virtualCareModel
    );

    if (
      preSelectEapFilter &&
      eapOption.length < 1 &&
      providerType !== ProviderType.ORGANIZATION
    ) {
      areaOfExpertise.push({
        label: t(EAP_OPTION_LABEL),
        value: eapOptionValue,
        dnt_label: EAP_OPTION_LABEL,
      });
    }

    const distanceAndAutoSearchRadius = !isVirtualCareSelected
      ? [
          FilterCollectionModel.AUTO_SEARCH_RADIUS,
          FilterCollectionModel.DISTANCE,
        ]
      : [];

    const virtualCareFilter =
      virtualCare || virtualCareCode ? [virtualCareModel] : [];

    if (!skipPreSelectingFilters) {
      const defaultFilters: FilterCollectionModel[] = [
        ...distanceAndAutoSearchRadius,
        ...planTypeRequired,
        ...(acceptingNewPatients
          ? [FilterCollectionModel.ACCEPTING_NEW_PATIENTS]
          : []),
        ...virtualCareFilter,
        ...(preferredFacility?.length > 0
          ? [FilterCollectionModel.PREFERRED_FACILITY]
          : []),
        ...(areaOfExpertise?.length > 0
          ? [FilterCollectionModel.AREA_OF_EXPERTISE]
          : []),
        ...(agesTreadedAoes?.length > 0
          ? [FilterCollectionModel.AGES_TREATED]
          : []),
        ...(specialtyCodeFromQuery?.length > 0
          ? [FilterCollectionModel.SPECIALTY_CODE]
          : []),
        ...(genderCode?.length > 0 ? [FilterCollectionModel.GENDER_CODE] : []),
        ...(essentialCommunityProviderCode
          ? [FilterCollectionModel.ESSENTIAL_COMMUNITY_PROVIDER]
          : []),
      ];

      setDefaultSearchFilters(defaultFilters, {
        autoSearchRadius: searchRadius,
        preferredFacility,
        AgesAreaOfExpertise: agesTreadedAoes,
        AreaOfExpertise: areaOfExpertise,
        SpecialtyCode: specialtyCodeFromQuery,
        GenderCode: genderCode,
        [virtualCareModel]: virtualCareCode,
        EssentialCommunityProvider: essentialCommunityProviderCode,
        SortBy: searchFilters?.SortBy,
      });
      setSearchParams(
        () => {
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set(Constants.PRE_SELECT_FILTERS, 'false');
          return searchParams;
        },
        { replace: true }
      );
    } else {
      setDefaultSearchFilters(
        [
          ...distanceAndAutoSearchRadius,
          ...planTypeRequired,
          ...virtualCareFilter,
        ],
        {
          autoSearchRadius: searchRadius,
          SortBy: searchFilters?.SortBy,
          VirtualCare: virtualCareCode,
        }
      );
    }

    setAnalyticsState({ linkName: viewAllDataTestId });
    setSearchStore({
      totalCount,
      includeOrgTypeCodes: orgTypeCodes?.includeOrgTypeCodes,
      excludeOrgTypeCodes: orgTypeCodes?.excludeOrgTypeCodes,
      searchRadius,
    });
    setPCPSearchState({
      pcpIndicator: resolvePcpIndicator(
        disablePCPIndAndANP,
        isPCPLandingPage,
        choosePCP
      ),
      dependentSeqNbr,
    });

    const typeaheadSearchStore = {
      specialtyCode,
      aoeCodes,
      sectionType,
      providerType,
      includeSpecialityRollupCodes: getIncludeSpecialityRollupCodes(
        searchType,
        includeSpecialityRollupCodesFilteredByCategory,
        combinedIncludeSpecialityRollupCodes,
        choosePCP
      ),
      search,
      acceptingNewPatients: choosePCP ? true : acceptingNewPatients,
      ...(categoryCoverageType && {
        coverageType: categoryCoverageType,
      }),
      invokeLlmModel: ternaryExpression(
        searchParams.get('fromLandingPage') === 'true',
        false,
        invokeLlmModel
      ),
      preSelectEapFilter,
      virtualCare,
      isTypeaheadStoreValuesSet: true,
      // Preserve networkAffiliationId from URL params for auto-selection in All Filters drawer
      ...(networkAffiliationIdFromParams
        ? { networkAffiliationId: [networkAffiliationIdFromParams] }
        : {}),
    };
    setTypeaheadSearchStore(typeaheadSearchStore);
    setIsProcessingResults(false);
  };

  const handleBehaviorHealthFilters = (
    selectedFilters: ProviderSearchFilters,
    distanceAndAutoSearchRadius: string[],
    searchRadius: number
  ) => {
    const selectedFiltersData = getBrowseServicesPreSelectedFilters({
      isBHFacility,
      virtualCare,
      section,
      formattedAOEIds,
      preSelectEapFilter,
      coverageType,
      searchRadius: compareAndReturnAsNumber(
        searchRadius,
        DEFAULT_SEARCH_RADIUS
      ),
      filters: transformedFilters,
      t,
    });
    const defaultFilters = setDefaultSearchFilters(
      [
        ...(distanceAndAutoSearchRadius as FilterCollectionModel[]),
        ...planTypeRequired,
      ],
      { autoSearchRadius: searchRadius, SortBy: searchFilters?.SortBy }
    );
    setSearchFilters({
      ...searchFilters,
      ...(!skipPreSelectingFilters ? selectedFiltersData : {}),
      ...defaultFilters,
    });
  };

  const handleLabsAndServicesFilters = (
    selectedFilters: ProviderSearchFilters,
    distanceAndAutoSearchRadius: string[],
    searchRadius: number
  ) => {
    const selectedFiltersData = getBrowseLabsAndServicesPreSelectFilters({
      section,
      filters: transformedFilters,
    });
    const defaultFilters = setDefaultSearchFilters(
      [
        ...(distanceAndAutoSearchRadius as FilterCollectionModel[]),
        ...planTypeRequired,
      ],
      { autoSearchRadius: searchRadius, SortBy: searchFilters?.SortBy }
    );
    setSearchFilters({
      ...searchFilters,
      ...(!skipPreSelectingFilters ? selectedFiltersData : {}),
      ...defaultFilters,
    });
  };

  const handleOtherCategoryFilters = (
    distanceAndAutoSearchRadius: string[],
    searchRadius: number,
    ANPFilterArray: string[],
    isVirtualCareSelectedFromParams: boolean
  ) => {
    const { coverages = [] } =
      Constants.CARE_CATEGORIES_NAME_PAYLOAD.find(
        (category) => category.label === chipValue
      ) || {};
    const categoryCoverageType = coverages?.[0];
    const virtualCareModel = getVirtualCareModel(
      enableBHVirtualCareFilterV2,
      categoryCoverageType,
      sectionType
    );
    const isVirtualCareV2 = getIsVirtualCareV2(
      enableBHVirtualCareFilterV2,
      categoryCoverageType,
      sectionType
    );
    const virtualCareCode = getVirtualCareCode(t, isVirtualCareV2);
    const defaultFilters = setDefaultSearchFilters(
      [
        ...(ANPFilterArray as FilterCollectionModel[]),
        ...(distanceAndAutoSearchRadius as FilterCollectionModel[]),
        ...planTypeRequired,
        ...ternaryExpression(
          isVirtualCareSelectedFromParams,
          [virtualCareModel] as FilterCollectionModel[],
          []
        ),
      ],
      {
        autoSearchRadius: searchRadius,
        SortBy: searchFilters?.SortBy,
        ...ternaryExpression(
          isVirtualCareSelectedFromParams,
          {
            [virtualCareModel]: virtualCareCode,
          },
          {}
        ),
      }
    );
    isVirtualCareSelectedFromParams &&
      setSearchFilters({
        ...searchFilters,
        ...ternaryExpression(!skipPreSelectingFilters, {}, {}),
        ...defaultFilters,
        Distance: getDistanceFilterOption(
          defaultFilters?.AutoSearchRadius || 0,
          defaultFilters?.AutoSearchRadius
        ),
      });
  };

  const onSnackCardCompleted = (params) => {
    setIsProcessingResults(true);
    if (isLandingPageSearch) {
      const { searchRadius: searchRadiusFromQuery, totalCount } = params;

      const searchRadius = compareAndReturnAsNumber(
        searchRadiusFromParams,
        searchRadiusFromQuery
      );

      const ANPFilterArray = getANPFilter({
        isPrimaryCareProvidersSearch,
        isPrimaryCareProviderGroupsSearch,
        isFqhAndrhcSearch,
        disablePCPIndAndANP,
      });

      const virtualCareModel = getVirtualCareModel(
        enableBHVirtualCareFilterV2,
        coverageType,
        sectionType
      );

      const isVirtualCareSelected = getIssVirtualCareSelected(
        searchFilters,
        virtualCareModel
      );
      const isVirtualCareSelectedFromParams =
        searchParams.get('virtualCare') === 'true';

      const distanceAndAutoSearchRadius = !(
        isVirtualCareSelected || isVirtualCareSelectedFromParams
      )
        ? [
            FilterCollectionModel.AUTO_SEARCH_RADIUS,
            FilterCollectionModel.DISTANCE,
          ]
        : [];

      const selectedFilters: ProviderSearchFilters = {};
      if (isBrowseBehaviorHealthProvidersSearch) {
        handleBehaviorHealthFilters(
          selectedFilters,
          distanceAndAutoSearchRadius,
          searchRadius
        );
      } else if (isBrowseLabsAndServicesSearch) {
        handleLabsAndServicesFilters(
          selectedFilters,
          distanceAndAutoSearchRadius,
          searchRadius
        );
      } else {
        handleOtherCategoryFilters(
          distanceAndAutoSearchRadius,
          searchRadius,
          ANPFilterArray,
          isVirtualCareSelectedFromParams
        );
      }

      setSearchParams(
        () => {
          const searchParams = new URLSearchParams(window.location.search);
          searchParams.set(Constants.PRE_SELECT_FILTERS, 'false');
          return searchParams;
        },
        { replace: true }
      );
      const typeaheadSearchStore = {
        search,
        searchType,
        fromViewAll: true,
        keyword: search,
        sectionType,
        providerType: getProviderTypeBySectionType(sectionType as SectionType),
        heading: getHeading({ psxSource, heading, chipValue }),
        isTypeaheadStoreValuesSet: true,
        isVisionRelatedSearch,
        ...(isPrimaryCareSearch
          ? { acceptingNewPatients: isPrimaryCareSearch }
          : {}),
        ...(isBrowseCommonSpecialtiesSearch
          ? { medicalSpeciality: isBrowseCommonSpecialtiesSearch }
          : {}),
        ...(isFqhAndrhcSearch
          ? {
              includeSpecialityRollupCodes: specialtyCode,
              combinedIncludeSpecialityRollupCodes: specialtyCode,
            }
          : {}),
        ...(isSpecialtyCodeSearch ? { specialtyCode } : {}),
        // Preserve networkAffiliationId from URL params for auto-selection in All Filters drawer
        ...(networkAffiliationIdFromParams
          ? { networkAffiliationId: [networkAffiliationIdFromParams] }
          : {}),
      };

      setTypeaheadSearchStore(typeaheadSearchStore, { replace: true });
      const rollupCodesForVCProviderSearch = getRollupCodeForVirtualPrimaryCare(
        chipValue,
        pseudoRollupCodeForPrimaryCareLandingPage,
        search,
        specialtyCode
      );
      const filteredVirtualCareProvidersWithDetails =
        getCategoryVirtualCareProvidersFromEligibilities({
          search,
          includeSpecialityRollupCodes: rollupCodesForVCProviderSearch,
          uesVirtualCareKeywordMappingTable,
          eligibilities: eligibilities[planToken],
          enablePseudoRollupCodeOnlyMatch,
          chipValue,
        });
      setVirtualCareProviders(filteredVirtualCareProvidersWithDetails);
      setPCPSearchState({ pcpIndicator });
      setSearchStore({
        totalCount,
        searchRadius,
        ...(isBrowseBehaviorHealthProvidersSearch
          ? { behavioralFacilityPrograms: programIDs }
          : {}),
        ...(isVisionCareSearch
          ? { onlineRetailers: getOnlineRetailers(section.isOnlineProviders) }
          : {}),
      });
    }
    setIsProcessingResults(false);
  };

  const rollupCodeParams = getRollupCodeParams({
    isFqhAndrhcSearch,
    isSpecialtyCodeSearch,
    specialtyCode,
    enableInclSpecCodeChanges,
  });

  const isBHFacility =
    section?.dntProviderType?.toLowerCase() ===
    ProviderTypes?.FACILITY.toLowerCase();

  const preSelectEapFilter =
    portalData?.portalConfig?.preSelectEapFilter && !isBHFacility;

  const parsedAOEIds = convertTypeStringToArray(aoeIDs);
  const formattedAOEIds: string[] = getUpdatedAoeCodesWithEAP(
    eapBHProgramFlag,
    preSelectEapFilter,
    parsedAOEIds,
    []
  );

  const behavioralProgramId = getBehavioralProgramId(
    eapBHProgramFlag,
    preSelectEapFilter
  );

  const {
    snackCardProviderResults: {
      isLoading: isSnackCardResultsLoading,
      data: { totalCount: totalCountFromSnackCardResults } = {},
    } = {},
    getSnackCardResults,
  } = useSnackCardProviderResults({
    ...rollupCodeParams,
    ...(isPrimaryCareSearch ? { isPCPLandingPage, pcpIndicator } : {}),
    providerType: getProviderTypeBySectionType(sectionType as SectionType),
    search,
    coverageType,
    onCompleted: onSnackCardCompleted,
    shouldGetHGData: sectionType === SectionType.PROVIDER,
    executeImmediately: false,
    platinumProgramCode: platinumProgramCodeFromParams,
    bhProgramFacilityAgg: bhProgramFacilityAggFromParams,
    ...(isBrowseLabsAndServicesSearch
      ? { behavioralFacilityPrograms: section?.bhFacilityProgramIds }
      : {}),
    ...(isBrowseBehaviorHealthProvidersSearch
      ? {
          aoeCodes: formattedAOEIds,
          behavioralProgramId,
          behavioralFacilityPrograms: programIDs,
          virtualCare: !!virtualCare,
        }
      : {}),
    ...(isVisionCareSearch
      ? { onlineRetailers: section.isOnlineProviders }
      : {}),
    ...(isSuggestedProvidersSearch ? { pcpIndicator: false } : {}),
  });

  useEffect(() => {
    if (isLandingPageSearch) {
      getSnackCardResults();
    }
  }, [isLandingPageSearch, getSnackCardResults]);

  useEffect(() => {
    if (mapLocationsOnly) {
      setTypeaheadSearchStore({ isTypeaheadStoreValuesSet: true });
    }
  }, [mapLocationsOnly]);

  const isExternalLinkMapView =
    !!sectionType &&
    !(
      psxSource ||
      choosePCP ||
      isLAWWOrSupportFinderDeeplink ||
      isGlobalSearch
    );

  useEffect(() => {
    if (isExternalLinkMapView && !isTypeaheadStoreValuesSet) {
      setTypeaheadSearchStore({ isTypeaheadStoreValuesSet: true });
      setDefaultSearchFilters([]);
    }
  }, [isExternalLinkMapView, isTypeaheadStoreValuesSet]);

  const {
    isLoading: isMixedResultsLoading,
    combinedSearchResults,
    providerIsLoading,
    orgIsLoading,
    medGrpIsLoading,
  } = useTypeAheadData({
    onCompleted: onMixedResultsPageResultsLoaded,
  });

  const mapPinsPageNumber = Math.ceil(
    (pageSize * pageNumber) / mapPinsPageSize
  );

  const getFilterProviderResultsParams = (
    mapLocationsOnly?: boolean,
    skipCall?: boolean
  ) => ({
    search,
    pageNumber: mapLocationsOnly ? mapPinsPageNumber : pageNumber,
    pageSize: mapLocationsOnly ? mapPinsPageSize : pageSize,
    selectedFilters: searchFilters,
    coverageType,
    shouldGetHGData: mapLocationsOnly ? false : shouldGetHGData,
    initialPage: false,
    mapLocationsOnly,
    skip: skipCall,
    rollupCodeParams,
    providerType,
    platinumProgramCode: platinumProgramCodeFromParams,
    bhProgramFacilityAgg: bhProgramFacilityAggFromParams,
    ...(providerId && { providerId }),
    ...(providerLocationId && { providerLocationId }),
    ...(extendedPinsDrawerReq && { extendedPinsDrawerReq }),
  });

  const filteredResults = useFilterProviderResults(
    getFilterProviderResultsParams()
  );

  const providerPinsResults = useFilterProviderResults(
    getFilterProviderResultsParams(enableMapPinRedesign, !enableMapPinRedesign)
  );

  const {
    results = [],
    totalResultsCount,
    isLoading: isProviderSearchResultsLoading = false,
    isTieredPlan,
    headers,
    recommendationPreferences,
  } = filteredResults;

  const {
    totalCountPractitioners,
    totalCountOrganizations,
    totalCountMedicalGroups,
  } = combinedSearchResults || {};

  const totalCountFromMixedResults =
    totalCountPractitioners ||
    totalCountOrganizations ||
    totalCountMedicalGroups;

  // if totalCountFromMixedResults or totalCountFromSnackCardResults has results,
  // then totalResultsCount should have results hence keep the loading state to
  // true until totalResultsCount is available.
  const isResultsLoading =
    totalCountFromMixedResults || totalCountFromSnackCardResults
      ? !totalResultsCount
      : false;

  const isLoading =
    isProviderSearchResultsLoading ||
    isSnackCardResultsLoading ||
    isMixedResultsLoading ||
    isProcessingResults ||
    providerIsLoading ||
    medGrpIsLoading ||
    orgIsLoading ||
    isResultsLoading;

  return {
    results,
    totalResultsCount: psxSource ? totalResultsCount || 0 : totalResultsCount,
    isLoading:
      psxSource || choosePCP ? isLoading : isProviderSearchResultsLoading,
    isTieredPlan,
    headers,
    initialPage: false,
    sectionType,
    providerPinsResults,
    recommendationPreferences,
  };
};
const getRollupCodeForVirtualPrimaryCare = (
  chipValue: string,
  pseudoRollupCodeForPrimaryCareLandingPage: any,
  search: string,
  specialtyCode: any
) =>
  chipValue?.includes(CareCategories.PRIMARY_CARE) &&
  pseudoRollupCodeForPrimaryCareLandingPage
    ? getRollupCodesForVCProviderSearch(
        search,
        specialtyCode,
        pseudoRollupCodeForPrimaryCareLandingPage
      )
    : specialtyCode;
